import { QueryClientProvider } from '@tanstack/react-query';
import { PropsWithChildren } from 'react';
import { IntlProvider } from 'react-intl';

import { useContactCacheInvalidation } from '@/hooks/useContactCacheInvalidation';
import { CRUDViewProvider } from '@/hooks/useCRUDView';
import { DataModelProvider } from '@/hooks/useDataModel';
import { IndexedDBProvider } from '@/hooks/useIndexedDB';
import { OrganizationProvider } from '@/hooks/useOrganization';
import { ToastProvider } from '@/hooks/useToast';
import { TranslationProvider } from '@/hooks/useTranslation';
import { UserProvider } from '@/hooks/useUser';
import { queryClient } from '@/services/query-client';

import { TooltipProvider } from './core/Tooltip';

const ContactCacheSubscriber = ({ children }: PropsWithChildren) => {
    useContactCacheInvalidation();
    return <>{children}</>;
};

/**
 * A page wrapper where we can inject various contexts into our page components without
 * the need to create different contexts for each component (i.e. a singleton for contexts).
 *
 * This would be removed once the migration is done.
 */
const ReactBridgePageWrapper = ({
    children,
}: PropsWithChildren<Record<never, never>>) => (
    <UserProvider>
        <OrganizationProvider>
            <DataModelProvider>
                <TooltipProvider>
                    <QueryClientProvider client={queryClient}>
                        <IndexedDBProvider>
                            <ContactCacheSubscriber>
                                <IntlProvider locale={navigator.language}>
                                    <TranslationProvider>
                                        <CRUDViewProvider>
                                            <ToastProvider>{children}</ToastProvider>
                                        </CRUDViewProvider>
                                    </TranslationProvider>
                                </IntlProvider>
                            </ContactCacheSubscriber>
                        </IndexedDBProvider>
                    </QueryClientProvider>
                </TooltipProvider>
            </DataModelProvider>
        </OrganizationProvider>
    </UserProvider>
);

export { ReactBridgePageWrapper };
